import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { appLogger } from '@/lib/logger';
import { recordSecurityEvent } from '@/lib/securityEvents';
import {
  PROFILE_AVATARS_BUCKET,
  buildProfileAvatarStoragePath,
  extractProfileAvatarStoragePath,
  getProfileAvatarMimeType,
  validateProfileAvatarFile,
} from '@/lib/profileAvatar';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import type { Database, Json, MemberPayrollDetails, OrgMember, Profile } from '@/types/database.types';

const PROFILE_CAPTURE_SELECT = `
  user_id,
  email,
  full_name,
  phone,
  avatar_url,
  dob,
  address_unit,
  address_street,
  address_suburb,
  address_state,
  address_postcode,
  address_country,
  residency_status,
  visa_subclass,
  visa_expiry_date,
  emergency_contact_name,
  emergency_contact_relationship,
  emergency_contact_phone,
  profile_confirmed,
  availability,
  availability_confirmed,
  created_at,
  updated_at
`;

export type EditableProfile = Pick<
  Profile,
  | 'user_id'
  | 'email'
  | 'full_name'
  | 'phone'
  | 'avatar_url'
  | 'dob'
  | 'address_unit'
  | 'address_street'
  | 'address_suburb'
  | 'address_state'
  | 'address_postcode'
  | 'address_country'
  | 'residency_status'
  | 'visa_subclass'
  | 'visa_expiry_date'
  | 'emergency_contact_name'
  | 'emergency_contact_relationship'
  | 'emergency_contact_phone'
  | 'profile_confirmed'
  | 'availability'
  | 'availability_confirmed'
  | 'created_at'
  | 'updated_at'
>;

type ProfileCaptureUpdate = Partial<
  Pick<
    Profile,
    | 'full_name'
    | 'phone'
    | 'address_unit'
    | 'address_street'
    | 'address_suburb'
    | 'address_state'
    | 'address_postcode'
    | 'address_country'
    | 'dob'
    | 'avatar_url'
    | 'residency_status'
    | 'visa_subclass'
    | 'visa_expiry_date'
    | 'emergency_contact_name'
    | 'emergency_contact_relationship'
    | 'emergency_contact_phone'
    | 'profile_confirmed'
    | 'availability_confirmed'
  >
>;

export type CurrentOrgMember = Pick<
  OrgMember,
  | 'id'
  | 'organization_id'
  | 'user_id'
  | 'role'
  | 'employee_code'
  | 'employment_type'
  | 'payment_frequency'
  | 'start_date'
  | 'super_rate'
  | 'deactivation_effective_at'
>;

export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: ['profile', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(PROFILE_CAPTURE_SELECT)
        .eq('user_id', userId!)
        .single();
      if (error) throw error;
      return data as EditableProfile;
    },
  });
}

export function useCurrentOrgMember() {
  const user = useAuthStore((s) => s.user);
  const orgId = useOrgStore((s) => s.organization?.id);

  return useQuery({
    queryKey: ['current-org-member', orgId, user?.id],
    enabled: !!orgId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('org_members')
        .select(
          'id, organization_id, user_id, role, employee_code, employment_type, payment_frequency, start_date, super_rate, deactivation_effective_at',
        )
        .eq('organization_id', orgId!)
        .eq('user_id', user!.id)
        .eq('is_active', true)
        .or(`deactivation_effective_at.is.null,deactivation_effective_at.gt.${new Date().toISOString()}`)
        .maybeSingle();
      if (error) throw error;
      return data as CurrentOrgMember | null;
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const orgId = useOrgStore((s) => s.organization?.id);

  return useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: ProfileCaptureUpdate;
    }) => {
      const { data, error } = await supabase
        .from('profiles')
        .upsert(
          {
            user_id: userId,
            ...(currentUser?.id === userId
              ? { email: currentUser.email?.trim().toLowerCase() || null }
              : {}),
            ...updates,
          },
          { onConflict: 'user_id' },
        )
        .select(PROFILE_CAPTURE_SELECT)
        .single();
      if (error) throw error;
      return data as EditableProfile;
    },
    onSuccess: (data) => {
      void recordSecurityEvent({
        organizationId: orgId,
        action: 'PROFILE_SENSITIVE_EDITED',
        subjectUserId: data.user_id,
      });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile-onboarding'] });
    },
  });
}

export function useUpdateProfileAvatar() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async ({
      userId,
      file,
      previousAvatarUrl,
    }: {
      userId: string;
      file: File;
      previousAvatarUrl?: string | null;
    }) => {
      if (!currentUser?.id || currentUser.id !== userId) {
        throw new Error('You can only update your own profile photo.');
      }

      const validationError = validateProfileAvatarFile(file);
      if (validationError) throw new Error(validationError);

      const mimeType = getProfileAvatarMimeType(file);
      if (!mimeType) throw new Error('Upload a supported profile photo.');

      const filePath = buildProfileAvatarStoragePath(userId, file);
      const { error: uploadError } = await supabase.storage
        .from(PROFILE_AVATARS_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          contentType: mimeType,
          upsert: false,
        });
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from(PROFILE_AVATARS_BUCKET)
        .getPublicUrl(filePath);
      const avatarUrl = publicUrlData.publicUrl;

      const { data, error } = await supabase
        .from('profiles')
        .upsert(
          {
            user_id: userId,
            email: currentUser.email?.trim().toLowerCase() || null,
            avatar_url: avatarUrl,
          },
          { onConflict: 'user_id' },
        )
        .select(PROFILE_CAPTURE_SELECT)
        .single();

      if (error) {
        await supabase.storage.from(PROFILE_AVATARS_BUCKET).remove([filePath]);
        throw error;
      }

      await updateAuthAvatarMetadata(avatarUrl, currentUser.user_metadata);
      await removePreviousAvatar(previousAvatarUrl, userId, filePath);

      return data as EditableProfile;
    },
    onSuccess: (data) => {
      invalidateProfileAvatarQueries(queryClient, data.user_id);
    },
  });
}

export function useRemoveProfileAvatar() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async ({
      userId,
      previousAvatarUrl,
    }: {
      userId: string;
      previousAvatarUrl?: string | null;
    }) => {
      if (!currentUser?.id || currentUser.id !== userId) {
        throw new Error('You can only remove your own profile photo.');
      }

      const { data, error } = await supabase
        .from('profiles')
        .upsert(
          {
            user_id: userId,
            email: currentUser.email?.trim().toLowerCase() || null,
            avatar_url: null,
          },
          { onConflict: 'user_id' },
        )
        .select(PROFILE_CAPTURE_SELECT)
        .single();

      if (error) throw error;

      await updateAuthAvatarMetadata(null, currentUser.user_metadata);
      await removePreviousAvatar(previousAvatarUrl, userId);

      return data as EditableProfile;
    },
    onSuccess: (data) => {
      invalidateProfileAvatarQueries(queryClient, data.user_id);
    },
  });
}

export function useUpdateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      availability,
    }: {
      userId: string;
      availability: Record<string, { available: boolean; start?: string; end?: string }>;
    }) => {
      const { data, error } = await supabase
        .from('profiles')
        .upsert({
          user_id: userId,
          availability: availability as unknown as Json,
          availability_confirmed: true,
        }, { onConflict: 'user_id' })
        .select(PROFILE_CAPTURE_SELECT)
        .single();
      if (error) throw error;
      return data as EditableProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile-onboarding'] });
    },
  });
}

async function updateAuthAvatarMetadata(
  avatarUrl: string | null,
  currentMetadata: Record<string, unknown> | undefined,
) {
  const { error } = await supabase.auth.updateUser({
    data: {
      ...(currentMetadata ?? {}),
      avatar_url: avatarUrl,
    },
  });

  if (error) {
    appLogger.warn('Profile avatar was saved but auth metadata was not updated.', error);
  }
}

async function removePreviousAvatar(
  previousAvatarUrl: string | null | undefined,
  userId: string,
  newPath?: string,
) {
  const previousPath = extractProfileAvatarStoragePath(previousAvatarUrl, userId);
  if (!previousPath || previousPath === newPath) return;

  const { error } = await supabase.storage.from(PROFILE_AVATARS_BUCKET).remove([previousPath]);
  if (error) {
    appLogger.warn('Profile avatar was saved but previous avatar cleanup failed.', error);
  }
}

function invalidateProfileAvatarQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
) {
  queryClient.invalidateQueries({ queryKey: ['profile', userId] });
  queryClient.invalidateQueries({ queryKey: ['profile-onboarding'] });
  queryClient.invalidateQueries({ queryKey: ['org-members'] });
  queryClient.invalidateQueries({ queryKey: ['managed-member-profile'] });
}

export function useMemberPayrollDetails(userId: string | undefined) {
  const orgId = useOrgStore((s) => s.organization?.id);

  return useQuery({
    queryKey: ['member-payroll-details', orgId, userId],
    enabled: !!orgId && !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('member_payroll_details')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('employee_user_id', userId!)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        void recordSecurityEvent({
          organizationId: orgId,
          action: 'PAYROLL_SENSITIVE_VIEWED',
          subjectUserId: userId,
        });
      }
      return data as MemberPayrollDetails | null;
    },
  });
}

export function useUpsertMemberPayrollDetails() {
  const queryClient = useQueryClient();
  const orgId = useOrgStore((s) => s.organization?.id);

  return useMutation({
    mutationFn: async (
      payload: Omit<Database['public']['Tables']['member_payroll_details']['Insert'], 'organization_id'>,
    ) => {
      if (!orgId) throw new Error('Organization is required to save payroll details.');

      const { data, error } = await supabase
        .from('member_payroll_details')
        .upsert(
          {
            ...payload,
            organization_id: orgId,
          },
          { onConflict: 'organization_id,employee_user_id' },
        )
        .select()
        .single();

      if (error) throw error;
      return data as MemberPayrollDetails;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['member-payroll-details', orgId, data.employee_user_id] });
      queryClient.invalidateQueries({ queryKey: ['managed-member-payroll-details', orgId, data.employee_user_id] });
      queryClient.invalidateQueries({ queryKey: ['profile-onboarding'] });
    },
  });
}
